import { useMemo } from 'react';
import { ConnectedEventInfo } from 'src/components/Events/ConnectedEventInfo';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionInfo } from 'src/WebApiController';

import { OnSaleDateBanner } from './OnSaleDateBanner';
import * as styles from './OnSaleEventDetails.css';
import { OnSaleEventDetailsHeader } from './OnSaleEventDetailsHeader';
import { OnSaleEventMetrics } from './OnSaleEventMetrics';
import { OnSaleEventPriceTracker } from './OnSaleEventPriceTracker';
import { OnSaleEventSectionSelection } from './OnSaleEventSectionSelection';
import { PerformerDescription } from './PerformerDescription';

export const OnSaleEventDetails = ({
  selectedSections,
  onSelectedSectionsChange,
}: {
  selectedSections?: SectionInfo[];
  onSelectedSectionsChange: (sections: SectionInfo[]) => void;
}) => {
  const { event: onSaleEvent, categoryResults } = useOnSaleEventsContext();
  const { event, performer, venue, eventAttributes } = onSaleEvent ?? {};

  const eventCategoryResult = useMemo(() => {
    if (!event?.perfId) {
      return null;
    }
    return categoryResults?.[event.perfId] ?? null;
  }, [categoryResults, event?.perfId]);

  return (
    <Stack
      direction="column"
      gap="s"
      width="full"
      height="full"
      className={styles.onSaleEventDetails}
    >
      <Stack
        direction="column"
        gap="m"
        width="full"
        height="full"
        className={styles.onSaleEventDetailsBody}
      >
        <OnSaleDateBanner
          onSaleDate={event?.onSaleDate ?? null}
          primary={eventAttributes?.inferredPrimary ?? null}
        />
        <Stack
          className={styles.onSaleEventCardContainer}
          width="full"
          direction="row"
          alignItems="center"
          gap="l"
        >
          <div
            className={styles.eventImageContainer}
            style={{
              backgroundImage: eventCategoryResult
                ? `url(${eventCategoryResult.imageUrl})`
                : undefined,
            }}
          >
            {eventCategoryResult?.imageUrl ? (
              <img
                alt="performer"
                src={eventCategoryResult?.imageUrl}
                width="80px"
                height="80px"
                className={styles.performerImage}
              />
            ) : (
              <div className={styles.dummyImage}>
                {(performer?.name ?? performer?.shortName ?? 'S').charAt(0)}
              </div>
            )}
          </div>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <ConnectedEventInfo
              showYear
              showEventDateTbd
              event={event}
              performer={performer}
              venue={venue}
              slimMode
              eventDateClassName={styles.eventCardVenueAndDateText}
              venueClassName={styles.eventCardVenueAndDateText}
            />
          </div>
        </Stack>
        <PerformerDescription />
        <OnSaleEventSectionSelection
          selectedSections={selectedSections}
          onSelectedSectionsChange={onSelectedSectionsChange}
        />
        <OnSaleEventMetrics selectedSections={selectedSections} />
        <OnSaleEventDetailsHeader titleContentId={ContentId.PriceTracker} />
        <OnSaleEventPriceTracker selectedSections={selectedSections ?? []} />
      </Stack>
    </Stack>
  );
};
